import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Loader2, Check, ChevronRight, Clock, ExternalLink, CalendarCheck, ArrowRightLeft } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { Markdown, useJsonLd } from "@/components/seo/Markdown";
import { DemoForm } from "@/components/seo/DemoForm";
import { ReviewsWidget } from "@/components/seo/ReviewsWidget";

const SITE = "https://theshopadvisor.com";
const soon = (v) => /coming soon/i.test(v || "");

const CompareTable = ({ p }) => (
  <div className="overflow-x-auto my-8 rounded-xl border border-slate-200" data-testid="compare-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-900 text-white"><tr><th className="text-left p-3">Feature</th><th className="text-left p-3">ShopAdvisor</th><th className="text-left p-3">{p.competitor}</th></tr></thead>
      <tbody>{p.rows.map((r, i) => (
        <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
          <td className="p-3 font-medium text-slate-800">{r[0]}</td>
          <td className={`p-3 font-semibold ${soon(r[1]) ? "text-amber-700" : "text-green-700"}`}><span className="inline-flex items-center gap-1">{soon(r[1]) ? <Clock className="w-4 h-4" /> : /^(yes|available)/i.test(r[1]) && <Check className="w-4 h-4" />}{r[1]}</span></td>
          <td className="p-3 text-slate-600">{r[2]}</td>
        </tr>))}</tbody>
    </table>
    {p.sources?.length > 0 && <div className="p-3 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="compare-sources"><b>Sources:</b>{p.sources.map(s => <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-blue-600 hover:underline">{s.label}<ExternalLink className="w-3 h-3" /></a>)}</div>}
    {p.disclaimer && <p className="text-xs text-slate-400 p-3 border-t border-slate-200" data-testid="compare-disclaimer">{p.disclaimer}</p>}
  </div>
);

const Features = ({ list }) => !list?.length ? null : (
  <div className="my-10" data-testid="seo-features"><h2 className="text-2xl font-bold text-slate-900 mb-4">What's included</h2>
    <div className="grid sm:grid-cols-2 gap-2">{list.map(f => (
      <div key={f.key} className="flex items-center gap-2 border border-slate-200 rounded-xl p-3 text-sm" data-testid={`seo-feature-${f.key}`}>
        {f.status === "available" ? <Check className="w-4 h-4 text-green-600 shrink-0" /> : <Clock className="w-4 h-4 text-amber-600 shrink-0" />}
        <span className="flex-1 text-slate-800">{f.name}</span>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${f.status === "available" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-800"}`}>{f.status === "available" ? "Available" : "Coming soon"}</span>
      </div>))}</div></div>
);

const Faqs = ({ faqs }) => (
  <div className="mt-12" data-testid="seo-faqs">
    <h2 className="text-2xl font-bold text-slate-900 mb-4">Frequently asked questions</h2>
    {faqs.map((f, i) => <details key={i} className="border-b border-slate-200 py-4 group"><summary className="font-semibold text-slate-900 cursor-pointer list-none flex justify-between">{f.q}<ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" /></summary><p className="text-slate-600 mt-2">{f.a}</p></details>)}
  </div>
);

const Ctas = ({ compare }) => (
  <div className="flex flex-wrap gap-3 mt-6">
    <a href="#demo" className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold px-5 py-2.5 rounded-xl inline-flex items-center gap-2" data-testid="seo-cta-demo"><CalendarCheck className="w-4 h-4" />Schedule a Demo</a>
    {compare ? <a href="#migration" className="border border-white/30 font-semibold px-5 py-2.5 rounded-xl hover:bg-white/10 inline-flex items-center gap-2" data-testid="seo-cta-migration"><ArrowRightLeft className="w-4 h-4" />Request Migration Consultation</a>
      : <Link to="/pricing" className="border border-white/30 font-semibold px-5 py-2.5 rounded-xl hover:bg-white/10" data-testid="seo-cta-pricing">See pricing</Link>}
  </div>
);

export default function SeoLandingPage({ kind, slug: fixed }) {
  const params = useParams();
  const slug = fixed || params.slug;
  const loc = useLocation();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const [p, setP] = useState(null);
  useEffect(() => { setP(null); api.get(preview ? `/api/seo/admin/pages/${slug}/preview` : `/api/seo/pages/${slug}`).then(r => setP(r.data)).catch(() => setP(false)); }, [slug, preview]);
  const canonical = p ? (p.canonical || `${SITE}${p.path}`) : undefined;
  usePageMeta(p?.meta_title, p?.meta_description, canonical);
  const isCompare = (p?.kind || kind) === "compare";
  const isGuide = (p?.kind || kind) === "guide";
  const base = isCompare ? "/compare" : isGuide ? "/guides" : "/solutions";
  const crumb = isCompare ? "Compare" : isGuide ? "Guides" : "Solutions";
  useJsonLd(p ? { "@context": "https://schema.org", "@graph": [
    { "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: SITE }, { "@type": "ListItem", position: 2, name: crumb, item: SITE + base }, { "@type": "ListItem", position: 3, name: p.headline, item: `${SITE}${p.path}` }] },
    { "@type": "FAQPage", mainEntity: (p.faqs || []).map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    { "@type": "SoftwareApplication", name: "The ShopAdvisor", applicationCategory: "BusinessApplication", operatingSystem: "Web", offers: { "@type": "Offer", price: "99", priceCurrency: "USD" } },
  ] } : null);
  if (p === false) return <Navigate to={base} replace />;
  if (!p) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!preview && p.path && p.path !== loc.pathname) return <Navigate to={p.path} replace />;
  const city = p.kind === "landing" && p.slug.startsWith("auto-repair-shop-software-") ? p.meta_title.replace("Auto Repair Shop Software in ", "") : "";
  return (
    <div className="bg-white" data-testid="seo-page">
      {preview && <div className="fixed top-16 inset-x-0 z-40 bg-amber-400 text-slate-900 text-center text-sm font-bold py-1" data-testid="seo-preview-banner">Draft preview — not published</div>}
      <section className="bg-slate-950 text-white pt-28 pb-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start [&>*]:min-w-0">
          <div>
            <nav className="text-xs text-slate-400 mb-4 flex items-center gap-1" data-testid="seo-breadcrumbs"><Link to="/" className="hover:text-white">Home</Link><ChevronRight className="w-3 h-3" /><Link to={base} className="hover:text-white">{crumb}</Link><ChevronRight className="w-3 h-3" /><span className="text-slate-300 truncate">{p.headline}</span></nav>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-yellow-400 mb-3">{p.region}</p>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-5" data-testid="seo-headline">{p.headline}</h1>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">{p.intro}</p>
            <Ctas compare={isCompare} />
          </div>
          <DemoForm id="demo" pageSlug={p.slug} pageTitle={p.headline} city={city} />
        </div>
      </section>
      <article className="max-w-3xl mx-auto px-6 py-14">
        {isCompare && p.rows && <CompareTable p={p} />}
        <Markdown>{p.body}</Markdown>
        <Features list={p.features} />
        {p.faqs?.length > 0 && <Faqs faqs={p.faqs} />}
        <ReviewsWidget />
        <div className={`mt-14 grid gap-6 [&>*]:min-w-0 ${isCompare ? "md:grid-cols-2" : ""}`}>
          <DemoForm dark pageSlug={p.slug} pageTitle={p.headline} city={city} title="Ready to see it in your shop?" />
          {isCompare && <DemoForm id="migration" intent="migration" pageSlug={p.slug} pageTitle={p.headline} />}
        </div>
      </article>
    </div>
  );
}
